import React from "react";
import { Link } from "react-router-dom";

const Home = () => {
  const services = [
    {
      title: "End of Tenancy",
      description:
        "Get your deposit back with our comprehensive move-out cleaning",
      icon: "🏠",
      color: "from-cyan-400 to-cyan-600",
      path: "/end-of-tenancy",
    },
    {
      title: "Commercial",
      description:
        "Professional cleaning for offices, retail spaces, and businesses",
      icon: "🏢",
      color: "from-navy-700 to-navy-900",
      path: "/commercial",
    },
    {
      title: "Residential",
      description: "Keep your home spotless with our flexible cleaning plans",
      icon: "✨",
      color: "from-cyan-500 to-navy-700",
      path: "/residential",
    },
    {
      title: "Locksmith",
      description: "24/7 emergency lockout service and security solutions",
      icon: "🔐",
      color: "from-coral-400 to-coral-500",
      path: "/locksmith",
    },
    {
      title: "Handyman",
      description: "Expert repairs, installations, and maintenance services",
      icon: "🔧",
      color: "from-navy-800 to-cyan-500",
      path: "/handyman",
    },
    {
      title: "More Services",
      description:
        "Carpet, windows, pressure washing, and specialised cleaning",
      icon: "⚡",
      color: "from-cyan-400 to-navy-800",
      path: "/additional-services",
    },
  ];

  const features = [
    { number: "10+", label: "Years Experience" },
    { number: "5000+", label: "Happy Clients" },
    { number: "24/7", label: "Support" },
    { number: "100%", label: "Satisfaction" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-white via-gray-50 to-cyan-50">
        {/* Geometric background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-48 -right-48 w-96 h-96 bg-gradient-to-br from-cyan-400/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute -bottom-48 -left-48 w-96 h-96 bg-gradient-to-tr from-navy-900/20 to-transparent rounded-full blur-3xl"></div>

          {/* Diagonal lines */}
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute top-1/4 -left-1/4 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent transform -rotate-12"></div>
            <div className="absolute top-2/3 -right-1/4 w-full h-1 bg-gradient-to-r from-transparent via-navy-900/20 to-transparent transform rotate-12"></div>
          </div>
        </div>

        <div className="relative container mx-auto px-4 lg:px-8 pt-32 pb-20">
          <div className="max-w-5xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-cyan-400/10 backdrop-blur-sm border border-cyan-400/30 rounded-full px-6 py-2 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
              <span className="text-sm font-bold text-navy-900 tracking-wide">
                TRUSTED BY 5000+ CLIENTS
              </span>
            </div>

            {/* Main headline */}
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-navy-900 leading-none mb-8">
              BRILLIANCE
              <br />
              <span className="bg-gradient-to-r from-cyan-400 to-navy-800 bg-clip-text text-transparent">
                IN EVERY
              </span>
              <br />
              DETAIL
            </h1>

            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
              Premium cleaning, locksmith, and handyman services that transform
              spaces and solve problems with precision and care.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-20">
              <Link
                to="/get-quote"
                className="group relative px-8 py-5 bg-navy-900 text-white font-bold text-lg rounded-xl overflow-hidden shadow-lg hover:shadow-2xl"
              >
                <span className="relative z-10">GET A FREE QUOTE</span>
              </Link>

              <a
                href="#services"
                className="px-8 py-5 bg-white border-2 border-navy-900 text-navy-900 font-bold text-lg rounded-xl hover:bg-navy-900 hover:text-white shadow-lg"
              >
                VIEW SERVICES
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {features.map((feature, index) => (
                <div key={index} className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/20 to-navy-900/20 rounded-2xl"></div>
                  <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200">
                    <div className="text-4xl font-black bg-gradient-to-br from-cyan-500 to-navy-900 bg-clip-text text-transparent mb-2">
                      {feature.number}
                    </div>
                    <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                      {feature.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-navy-900 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section
        id="services"
        className="py-24 bg-white relative overflow-hidden"
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, #00d9ff 35px, #00d9ff 37px)`,
            }}
          ></div>
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-navy-900 mb-6">
              OUR <span className="text-cyan-500">SERVICES</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive solutions for all your cleaning and maintenance
              needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div key={index} className="group">
                <Link to={service.path} className="block">
                  <div className="relative h-full bg-white rounded-2xl border-2 border-gray-200 overflow-hidden hover:border-cyan-400 shadow-lg hover:shadow-2xl">
                    {/* Gradient background */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-10`}
                    ></div>

                    {/* Diagonal accent */}
                    <div
                      className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${service.color} transform rotate-45 translate-x-12 -translate-y-12`}
                    ></div>

                    <div className="relative p-8">
                      <div className="text-5xl mb-4">{service.icon}</div>
                      <h3 className="text-2xl font-black text-navy-900 mb-3 group-hover:text-cyan-500">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-6">
                        {service.description}
                      </p>
                      <div className="flex items-center text-cyan-500 font-bold">
                        Learn More
                        <svg
                          className="w-5 h-5 ml-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white relative overflow-hidden">
        {/* Geometric accents */}
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black mb-6">
              WHY <span className="text-cyan-400">CHOOSE US</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Experience the difference of working with true professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Trusted Professionals",
                description:
                  "Fully vetted, insured, and trained team members you can rely on",
                icon: "🛡️",
              },
              {
                title: "Quality Guaranteed",
                description:
                  "100% satisfaction guarantee or we come back for free",
                icon: "⭐",
              },
              {
                title: "Flexible Scheduling",
                description:
                  "Available when you need us, including evenings and weekends",
                icon: "📅",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-navy-800/50 backdrop-blur-sm border border-cyan-400/30 rounded-2xl p-8 hover:border-cyan-400"
              >
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-2xl font-bold text-cyan-400 mb-4">
                  {item.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-navy-900 mb-6">
              OUR <span className="text-cyan-500">WORK</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See the transformations we deliver for our clients
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              "/gallery/IMG_7366.webp",
              "/gallery/IMG_7254.webp",
              "/gallery/IMG_7367.webp",
              "/gallery/IMG_7811.webp",
              "/gallery/IMG_7814.webp",
              "/gallery/IMG_8068.webp",
            ].map((item, index) => (
              <div key={index} className="group relative">
                <div className="aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-cyan-400/20 via-navy-900/10 to-cyan-500/20 border-2 border-gray-200 hover:border-cyan-400 transition-all shadow-lg hover:shadow-2xl">
                  <img
                    src={item}
                    alt={`Our Work`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/gallery"
              className="inline-flex items-center px-8 py-4 bg-navy-900 text-white font-bold text-lg rounded-xl hover:bg-navy-800 transition-colors shadow-lg"
            >
              View Full Gallery
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-cyan-400 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, white 35px, white 37px)`,
            }}
          ></div>
        </div>

        <div className="relative container mx-auto px-4 lg:px-8 text-center">
          <div>
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
              READY TO GET STARTED?
            </h2>
            <p className="text-xl text-navy-900 font-semibold mb-8 max-w-2xl mx-auto">
              Get a free, no-obligation quote in under 60 seconds
            </p>
            <Link
              to="/get-quote"
              className="px-12 py-6 bg-navy-900 text-white font-black text-xl rounded-xl shadow-2xl hover:bg-navy-800"
            >
              GET FREE QUOTE
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
